import React, { useMemo } from "react"

import { Subscription } from "@rails/actioncable"

import { WithLoadingIndicator } from "~tailwindui/Basics"

import { gql, useClient, useQuery } from "hooks/urql"
import useWebsocket from "hooks/useWebsocket"

import CaptionView from "components/AudioRoom/CaptionView"
import MuteButton from "components/AudioRoom/MuteButton"
import RosterSidebar from "components/AudioRoom/RosterSidebar"
import { ChimeJoinInfo } from "components/AudioRoom/types"
import useAudioRoom from "components/AudioRoom/useAudioRoom"
import useTranscriptForwarding from "components/ChimeMeetingHooks/useTranscriptForwarding"
import TeacherAideHistory from "components/Pages/Teacher/AudioRooms/TeacherAideHistory"

type AdminRoomProps = {
  joinInfo: ChimeJoinInfo
  onFatalError: (message: string) => void
  audioRoomId: string
  currentUserId: string
  audioInputDeviceId: string
  audioInputDeviceName: string
  controlsUnlockedUserIds: string[]
}

const AdminRoom: React.FC<AdminRoomProps> = ({
  joinInfo,
  onFatalError,
  audioRoomId,
  currentUserId,
  audioInputDeviceId,
  audioInputDeviceName,
  controlsUnlockedUserIds: initialControlsUnlockedUserIds,
}) => {
  const { connected, muted, mute, unmute, roster, audioVideo } = useAudioRoom(
    joinInfo,
    onFatalError,
    audioRoomId,
    currentUserId,
    audioInputDeviceId
  )
  const channelProps = useMemo(() => ({ id: audioRoomId }), [audioRoomId])
  const { channel }: { channel: Subscription } = useWebsocket({
    channelName: "AudioRoomChannel",
    channelProps,
  })
  const client = useClient()
  const [result] = useQuery({
    query: roomHistoryQuery,
    client,
    variables: { audioRoomId },
  })

  useTranscriptForwarding({
    enabled: true,
    audioVideo,
    userId: String(currentUserId),
    channel,
  })

  return (
    <>
      <audio className="hidden" id="room-audio" />
      <div className="grid h-screen grid-cols-[auto_minmax(0,1fr)]">
        <RosterSidebar
          audioRoomId={audioRoomId}
          roster={roster}
          localAttendeeId={joinInfo.attendee.attendeeId}
          connected={connected}
          channel={channel}
          initialControlsUnlockedUserIds={initialControlsUnlockedUserIds}
        />
        <WithLoadingIndicator result={result}>
          {({ data: { audioRoom } }) => (
            <main className="grid min-h-0 grid-rows-2">
              <TeacherAideHistory
                responses={audioRoom.activeAcrClassPeriod?.copilotResponses}
                userLabel={audioRoom.teacher.fullName}
              />

              <section className="relative min-h-0 border-t-4 border-solid border-neutral-700">
                <CaptionView
                  audioVideo={audioVideo}
                  roster={roster}
                  historyChunks={audioRoom.recentTranscriptChunks}
                />
                <MuteButton
                  connected={connected}
                  muted={muted}
                  toggleMute={() => (muted ? unmute : mute)()}
                  microphoneName={audioInputDeviceName}
                />
              </section>
            </main>
          )}
        </WithLoadingIndicator>
      </div>
    </>
  )
}

const roomHistoryQuery = gql`
  query AdminAudioRoomHistory($audioRoomId: ID!) {
    audioRoom(id: $audioRoomId) {
      activeAcrClassPeriod {
        copilotResponses {
          prompt
          response
        }
      }
      teacher {
        id
        fullName
      }
      recentTranscriptChunks {
        id
        speakerName
        text
        vocabularyFilterMatches
      }
    }
  }
`

export default AdminRoom
